import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function humanNum(n) {
  return (n || 0).toLocaleString("en-IN");
}

const MONTH_LABELS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function VacancyTrendsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Update client-side page title + meta on route mount (matches SSR values)
    const prevTitle = document.title;
    const monthLabel = new Date().toLocaleString("en-IN", { month: "long", year: "numeric" });
    document.title = `Indian Government Vacancy Trends ${monthLabel} | hireds.in`;
    axios
      .get(`${API}/vacancy-trends`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.message || "load failed"));
    return () => {
      document.title = prevTitle;
    };
  }, []);

  if (error) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-10">
        <p className="text-sm text-red-600">Unable to load vacancy trends right now. Please retry in a few seconds.</p>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="h-40 bg-slate-100 rounded-xl animate-pulse" />
      </main>
    );
  }

  const totals = data.totals || {};
  const bySector = data.by_sector || [];
  const byState = (data.by_state || []).slice(0, 12);
  const byMonth = data.by_month || [];
  const topOrgs = (data.top_organizations || []).slice(0, 10);

  const maxSector = Math.max(1, ...bySector.map((s) => s.posts || 0));
  const maxState = Math.max(1, ...byState.map((s) => s.posts || 0));
  const maxMonth = Math.max(1, ...byMonth.map((m) => m.posts || 0));

  const genMonth = new Date().toLocaleString("en-IN", { month: "long", year: "numeric" });

  return (
    <main className="max-w-6xl mx-auto px-4 py-10" data-testid="vacancy-trends-page">
      <nav className="text-xs text-slate-500 mb-3">
        <a href="/" className="hover:underline">Home</a> &rsaquo; <span className="text-slate-700">Vacancy Trends</span>
      </nav>

      <section className="rounded-2xl p-6 md:p-8 bg-slate-900 text-white mb-8" aria-labelledby="vt-h1">
        <h1 id="vt-h1" className="text-3xl md:text-4xl font-extrabold leading-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>
          Indian Government Vacancy Trends &mdash; {genMonth}
        </h1>
        <p className="text-slate-300 text-sm md:text-base max-w-3xl">
          Live snapshot of open Indian government vacancies across sectors, states and organisations. Data is compiled from official recruitment notifications tracked daily by the hireds.in editorial team.
        </p>
        <p className="text-slate-400 text-xs mt-3">
          Last updated: {(data.generated_at || "").slice(0, 10)} &middot; Editor: Dileshwar Vitthale &middot; Licence: CC BY 4.0 (cite hireds.in when reusing)
        </p>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="vt-stat-grid">
        <div className="p-4 border border-slate-200 rounded-xl bg-white">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Total posts tracked</div>
          <div className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>{humanNum(totals.total_posts)}</div>
        </div>
        <div className="p-4 border border-slate-200 rounded-xl bg-white">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Active notifications</div>
          <div className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>{humanNum(totals.total_openings)}</div>
        </div>
        <div className="p-4 border border-slate-200 rounded-xl bg-white">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Sectors covered</div>
          <div className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>{bySector.length}</div>
        </div>
        <div className="p-4 border border-slate-200 rounded-xl bg-white">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">States covered</div>
          <div className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>{(data.by_state || []).length}</div>
        </div>
      </section>

      <section className="mb-10" aria-labelledby="vt-sector-h">
        <h2 id="vt-sector-h" className="text-xl md:text-2xl font-extrabold mb-2 text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>Vacancies by sector</h2>
        <p className="text-sm text-slate-600 mb-4">State government tracks (Talathi, Lekhpal, VLW, Gramsevak) usually dominate by volume; central posts (SSC, RRB, IBPS) dominate by search interest.</p>
        <ol className="list-none p-0 m-0">
          {bySector.slice(0, 12).map((s) => {
            const pct = Math.max(3, Math.round(((s.posts || 0) / maxSector) * 100));
            const label = String(s.sector || "other").replace(/-/g, " ");
            return (
              <li key={s.sector} className="py-2 border-b border-slate-100" data-testid={`vt-sector-${s.sector}`}>
                <div className="flex justify-between text-sm font-semibold text-slate-900 capitalize mb-1">
                  <span>{label}</span>
                  <span className="font-normal text-slate-500 text-xs">{humanNum(s.posts)} posts &middot; {humanNum(s.openings)} notifications</span>
                </div>
                <div className="relative w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #f59e0b, #ea580c)" }} />
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="mb-10" aria-labelledby="vt-state-h">
        <h2 id="vt-state-h" className="text-xl md:text-2xl font-extrabold mb-2 text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>Vacancies by state</h2>
        <p className="text-sm text-slate-600 mb-4">Population-heavy states such as Uttar Pradesh, Maharashtra, Bihar and Madhya Pradesh usually publish the largest single-notification vacancies.</p>
        <ol className="list-none p-0 m-0">
          {byState.length === 0 && <li className="text-sm text-slate-500">No state-tagged jobs currently live.</li>}
          {byState.map((s) => {
            const pct = Math.max(3, Math.round(((s.posts || 0) / maxState) * 100));
            return (
              <li key={s.state} className="py-2 border-b border-slate-100">
                <div className="flex justify-between text-sm font-semibold text-slate-900 mb-1">
                  <span>{s.state}</span>
                  <span className="font-normal text-slate-500 text-xs">{humanNum(s.posts)} posts</span>
                </div>
                <div className="relative w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #f59e0b, #ea580c)" }} />
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="mb-10" aria-labelledby="vt-month-h">
        <h2 id="vt-month-h" className="text-xl md:text-2xl font-extrabold mb-2 text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>Vacancies published per month (last 12 months)</h2>
        <p className="text-sm text-slate-600 mb-4">Monthly volume of new recruitment notifications tracked by hireds.in. Look for the pre-election bump (Jan&ndash;Apr) and the post-monsoon central-govt cycle (Aug&ndash;Nov).</p>
        <div className="grid gap-1.5 items-end pt-4 pb-2 h-[220px] border-b border-slate-200" style={{ gridTemplateColumns: `repeat(${Math.max(byMonth.length, 1)}, 1fr)` }} role="img" aria-label="Bar chart of vacancies per month">
          {byMonth.map((m) => {
            const h = Math.max(6, Math.round(((m.posts || 0) / maxMonth) * 170));
            const [y, mm] = String(m.month).split("-");
            const shortMonth = MONTH_LABELS[parseInt(mm, 10)] || m.month;
            return (
              <div key={m.month} className="flex flex-col items-center justify-end" title={`${m.month}: ${humanNum(m.posts)} posts`}>
                <span className="text-[10px] font-bold text-slate-900 mb-1">{humanNum(m.posts)}</span>
                <div className="w-full rounded-t" style={{ height: `${h}px`, background: "linear-gradient(180deg,#0f172a,#1e293b)" }} />
                <span className="text-[10px] text-slate-500 mt-1.5 text-center">{shortMonth} {(y || "").slice(-2)}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mb-10" aria-labelledby="vt-org-h">
        <h2 id="vt-org-h" className="text-xl md:text-2xl font-extrabold mb-2 text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', system-ui" }}>Top 10 recruiting organisations</h2>
        <p className="text-sm text-slate-600 mb-4">Which departments are hiring at the largest volume in the current cycle.</p>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-2.5">Organisation</th>
                <th className="text-right px-4 py-2.5">Notifications</th>
                <th className="text-right px-4 py-2.5">Total posts</th>
              </tr>
            </thead>
            <tbody>
              {topOrgs.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-3 text-slate-500">No data yet.</td></tr>
              )}
              {topOrgs.map((o) => (
                <tr key={o.organization} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-medium text-slate-900">{o.organization}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{humanNum(o.openings)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900">{humanNum(o.posts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-8 p-5 bg-orange-50 border border-orange-200 rounded-xl text-sm text-orange-950">
        <p><strong className="text-orange-800">Journalists &amp; career bloggers:</strong> This data is licensed under <a className="underline" href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">CC BY 4.0</a>. Pull the same numbers from our JSON endpoint: <code className="bg-white border border-orange-200 px-1.5 py-0.5 rounded text-xs">/api/vacancy-trends</code>. When reusing please attribute to hireds.in.</p>
      </section>
    </main>
  );
}
